import { createFileRoute, redirect, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useCallback } from "react";
import { DashboardContext, DashboardLayout } from "@/components/dashboard-layout";
import { LicenceTable } from "./-components/licence-table";
import { licencesQueryOptions } from "@/server/licences";
import { SidebarLicence } from "./-components/licence-sidebar";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import { CrudEntry } from "@/lib/mma/types.gen";
import {
  checkIfUserMayRead,
  ContextUser,
  checkIfUserMayWrite,
  checkIfUserMayDelete,
  getNextRedirectRoute,
} from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/licences")({
  component: () => (
    <DashboardLayout submenu={null}>
      <LicencesRouteComponent />
    </DashboardLayout>
  ),
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;
    const mayRead = checkIfUserMayRead(user?.profile, user?.accessControl, "LICENCE");

    if (!mayRead) {
      const nextRedirectRoute = getNextRedirectRoute(user);
      throw redirect({ to: nextRedirectRoute });
    }
    return { title: "Licences" };
  },
  loader: ({ context }: { context: unknown }) => ({
    crumb: "Licences",
    userProfileId: (context as { user?: { profile: string } }).user?.profile,
    accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
  }),
});

function LicencesRouteComponent() {
  const navigate = useNavigate();
  const { userProfileId, accessControl } = Route.useLoaderData();
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "LICENCE");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "LICENCE");
  const searchParams = useSearch({ strict: false }) as {
    id?: string;
    search?: string;
    expire?: string;
  };
  const queryClient = useQueryClient();
  const licencesQuery = useQuery(licencesQueryOptions);
  const licences = licencesQuery.data;
  const context = useContext(DashboardContext);
  const closeRightSidebar = useRightSidebarClose();
  const openRightSidebar = useRightSidebarOpen();

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  const handleLicenceCreated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["licences"] });

    closeRightSidebar("id");
  }, [queryClient, closeRightSidebar]);

  const handleCancelSidebar = useCallback(() => {
    closeRightSidebar("id");
  }, [closeRightSidebar]);

  // Build sidebar component based on search params
  const sidebarComponent = useMemo(() => {
    if (searchParams.id === "new") {
      return (
        <SidebarLicence onLicenceCreated={handleLicenceCreated} onCancel={handleCancelSidebar} mayWrite={mayWrite} />
      );
    }
    return null;
  }, [searchParams.id, handleLicenceCreated, handleCancelSidebar, mayWrite]);

  // Handle sidebar opening/closing based on search params
  useEffect(() => {
    if (!context) {
      return;
    }

    const hasPermission = searchParams.id !== "new" || mayWrite;

    openRightSidebar({
      component: sidebarComponent,
      paramKeys: "id",
      shouldClose: false,
      permissionCheck: !hasPermission,
      onPermissionDenied: () => closeRightSidebar("id"),
    });
  }, [sidebarComponent, mayWrite, searchParams.id, openRightSidebar, closeRightSidebar]);

  const handleAddLicence = () => {
    navigate({
      to: "/licences",
      replace: true,
      search: (prev) => ({ ...prev, id: "new" }),
    });
  };

  return (
    <LicenceTable
      licences={licences}
      handleAddLicence={handleAddLicence}
      isLoading={licencesQuery.isLoading}
      mayWrite={mayWrite}
      mayDelete={mayDelete}
    />
  );
}
